/* ==========================================================================
   Virsaay — Scroll-Driven Editorial Collection Carousel Controller

   The small card nested in the headline expands to a full-bleed stage, then
   three slides wipe through while the copy travels right -> centre -> left.

   Performance notes (why it is built this way):
   - Nothing animates layout. The expansion is a clip-path on the frame plus a
     translate/scale on the media inside it; the travelling copy moves with
     `x`. Animating top/left/width/height re-laid out the page every frame,
     which is what made it stutter, especially on phones.
   - The section height is a fixed pixel value taken from the viewport once
     (and again only when the width changes). `100dvh` changes every time the
     mobile address bar moves, which desynced the pin spacer and left a gap
     of empty space after the third slide.
   - When motion is unavailable (reduced motion, GSAP blocked) the section
     becomes a regular carousel: the dots switch slides, nothing is pinned.
   - Touch screens hold the page only for the opening (card -> first film);
     the second and third films are behind the arrows.
   ========================================================================== */

(function () {
  'use strict';

  if (window.__virsaayCarouselLoaded) return;
  window.__virsaayCarouselLoaded = true;

  // Pinned scroll distance, in viewport heights, for the whole sequence.
  var SCROLL_VIEWPORTS = 3.2;
  // Where each slide is fully settled on the timeline — used by the dots.
  var SLIDE_PROGRESS = [0.28, 0.6, 0.94];
  // Touch: the swipe gate only holds the opening — the card in the headline,
  // then the first slide with its copy settled. The arrows move between the
  // slides (TOUCH_SLIDES) without holding the page.
  var STEP_STOPS = [0, 0.3];
  var TOUCH_SLIDES = [0.3, 0.6, 1];
  var FULL_CLIP = 'inset(0px 0px 0px 0px round 0px)';

  function onMotion(callback) {
    if (window.VirsaayMotion && window.VirsaayMotion.ready) {
      window.VirsaayMotion.ready(callback);
      return;
    }
    var attempts = 0;
    var timer = setInterval(function () {
      attempts++;
      if (window.VirsaayMotion && window.VirsaayMotion.ready) {
        clearInterval(timer);
        window.VirsaayMotion.ready(callback);
      } else if (attempts > 60) {
        clearInterval(timer);
        callback(window.gsap, window.ScrollTrigger);
      }
    }, 50);
  }

  function isReduced() {
    if (window.VirsaayMotion) return !!window.VirsaayMotion.reduced;
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // Offset of `el` inside `ancestor`, ignoring transforms (the intro backdrop
  // is scaled during the sequence; getBoundingClientRect would include that).
  function offsetWithin(el, ancestor) {
    var x = 0;
    var y = 0;
    var node = el;
    while (node && node !== ancestor) {
      x += node.offsetLeft;
      y += node.offsetTop;
      node = node.offsetParent;
    }
    return { x: x, y: y, w: el.offsetWidth, h: el.offsetHeight };
  }

  function initCarousel(section) {
    if (!section || section._vcc) return;

    var stage = section.querySelector('[data-vcc-stage]');
    var frame = section.querySelector('[data-vcc-expand-frame]');
    if (!stage || !frame) return;

    var media = section.querySelector('[data-vcc-media]');
    var intro = section.querySelector('[data-vcc-intro]');
    var spacer = section.querySelector('[data-vcc-spacer]');
    var scrim = section.querySelector('.vcc-scrim');
    var counter = section.querySelector('[data-vcc-counter]');
    var textAnchor = section.querySelector('[data-vcc-text-anchor]');
    var frames = [1, 2, 3].map(function (i) { return section.querySelector('[data-vcc-frame="' + i + '"]'); });
    var imgs = [1, 2, 3].map(function (i) { return section.querySelector('.vcc-img--' + i); });
    var texts = [1, 2, 3].map(function (i) { return section.querySelector('[data-vcc-slide-text="' + i + '"]'); });
    var dots = Array.prototype.slice.call(section.querySelectorAll('[data-vcc-dot]'));
    var prevBtn = section.querySelector('[data-vcc-prev]');
    var nextBtn = section.querySelector('[data-vcc-next]');

    var state = { active: -1, trigger: null, ctx: null, width: window.innerWidth };
    section._vcc = state;

    // Arrows: the static carousel just switches slides; the touch sequence
    // replaces this with a tween (see stepThrough).
    state.slideTo = function (index) { setActive(index); };
    function arrowState(index) {
      if (prevBtn) prevBtn.disabled = index <= 0;
      if (nextBtn) nextBtn.disabled = index >= 2;
    }
    if (prevBtn) prevBtn.addEventListener('click', function () { state.slideTo(Math.max(0, state.active - 1)); });
    if (nextBtn) nextBtn.addEventListener('click', function () { state.slideTo(Math.min(2, state.active + 1)); });

    function setHeight() {
      section.style.height = Math.max(560, window.innerHeight) + 'px';
    }

    function setActive(index) {
      if (index === state.active) return;
      state.active = index;
      for (var i = 0; i < 3; i++) {
        var on = i === index;
        if (texts[i]) texts[i].classList.toggle('is-active', on);
        if (frames[i]) frames[i].classList.toggle('is-active', on);
        if (dots[i]) {
          dots[i].classList.toggle('is-active', on);
          if (on) dots[i].setAttribute('aria-current', 'true');
          else dots[i].removeAttribute('aria-current');
        }
      }
      if (counter) counter.textContent = '0' + (index + 1) + ' / 03';
      arrowState(index);
    }

    dots.forEach(function (dot, index) {
      dot.addEventListener('click', function () {
        if (state.touch) return;
        var st = state.trigger;
        if (st) {
          var top = st.start + (st.end - st.start) * SLIDE_PROGRESS[index];
          window.scrollTo({ top: Math.round(top), behavior: isReduced() ? 'auto' : 'smooth' });
        } else {
          setActive(index);
        }
      });
    });

    function makeStatic() {
      section.classList.remove('is-animated');
      section.classList.add('is-static', 'is-expanded', 'is-ready');
      setActive(0);
    }

    // Phones run the same pinned sequence as desktop. The pinned height is
    // taken from innerHeight once and only re-read when the width changes
    // (ScrollTrigger ignores mobile resizes), so the address bar showing and
    // hiding cannot desync the pin.
    setHeight();

    onMotion(function (gsap, ScrollTrigger) {
      if (!gsap || !ScrollTrigger || isReduced()) {
        makeStatic();
        return;
      }

      gsap.registerPlugin(ScrollTrigger);
      section.classList.add('is-animated');
      var touch = window.matchMedia('(pointer: coarse)').matches;
      state.touch = touch;
      section.classList.toggle('is-touch', touch);

      function stageW() { return stage.clientWidth; }
      function stageH() { return stage.clientHeight; }

      // The thumbnail rectangle, inside the stage.
      function thumbRect() {
        if (spacer && spacer.offsetWidth > 0) return offsetWithin(spacer, stage);
        var w = stageW() < 990 ? 110 : 200;
        var h = stageW() < 990 ? 145 : 260;
        return { x: (stageW() - w) / 2, y: (stageH() - h) / 2, w: w, h: h };
      }

      function thumbClip() {
        var r = thumbRect();
        var right = stageW() - (r.x + r.w);
        var bottom = stageH() - (r.y + r.h);
        return 'inset(' + r.y + 'px ' + right + 'px ' + bottom + 'px ' + r.x + 'px round 4px)';
      }

      // Scale + translate the full-bleed media so it cover-fits the thumbnail,
      // exactly as if the image had been sized to the small card.
      function thumbScale() {
        var r = thumbRect();
        return Math.max(r.w / stageW(), r.h / stageH());
      }
      function thumbX() {
        var r = thumbRect();
        return r.x + r.w / 2 - (thumbScale() * stageW()) / 2;
      }
      function thumbY() {
        var r = thumbRect();
        return r.y + r.h / 2 - (thumbScale() * stageH()) / 2;
      }

      // Copy travels right -> centre -> left on desktop; stays centred on mobile.
      function copyX(position) {
        if (window.innerWidth < 990) return 0;
        return (position - 0.5) * stageW();
      }

      // On touch the opening cannot be skipped. It works as a gate: until the
      // card has opened, the page is not allowed below the section's top
      // (and, coming back up, above its bottom), however hard it is flung.
      // At the gate the page is frozen and a swipe opens the card onto the
      // first film; the next swipe lets the page go again. The other two
      // films are behind the arrows.
      function stepThrough(tl) {
        var root = document.documentElement;
        var index = 0;
        var slide = 0;
        var busy = false;
        var engaged = false;
        var heldAt = 0;
        var calmUntil = 0;
        // 'above': the sequence is still to come; 'below': it has played.
        var side = 'above';

        var pin = ScrollTrigger.create({
          trigger: section,
          pin: true,
          pinSpacing: true,
          start: 'top top',
          end: '+=120'
        });

        var intent = ScrollTrigger.observe({
          target: window,
          type: 'touch,wheel',
          wheelSpeed: -1,
          tolerance: 12,
          preventDefault: true,
          // Taps on DISCOVER and the dots must still go through.
          allowClicks: true,
          onUp: function () { go(index + 1); },
          onDown: function () { go(index - 1); }
        });
        intent.disable();

        // Freezing the root also stops iOS momentum mid-fling.
        function freeze(on) {
          root.style.overflow = on ? 'hidden' : '';
          root.style.overscrollBehavior = on ? 'none' : '';
        }

        function stop(step) {
          return step === 0 ? 0 : TOUCH_SLIDES[slide];
        }

        function engage(at, step) {
          engaged = true;
          index = step;
          tl.progress(stop(step));
          heldAt = at;
          window.scrollTo(0, at);
          freeze(true);
          calmUntil = Date.now() + 450;
          intent.enable();
        }

        function release(forward) {
          engaged = false;
          side = forward ? 'below' : 'above';
          intent.disable();
          freeze(false);
          window.scrollTo(0, forward ? pin.end + 2 : pin.start - 2);
        }

        function gate() {
          var y = window.scrollY;
          // Held: anything that still moves the page (iOS momentum) is undone.
          if (engaged) {
            if (y !== heldAt) window.scrollTo(0, heldAt);
            return;
          }
          if (side === 'above' && y > pin.start) engage(pin.start + 1, 0);
          else if (side === 'below' && y < pin.end) engage(pin.end - 1, STEP_STOPS.length - 1);
        }

        side = window.scrollY > pin.end ? 'below' : 'above';
        window.addEventListener('scroll', gate, { passive: true });
        gate();

        function go(next) {
          if (busy || Date.now() < calmUntil) return;
          if (next < 0) return release(false);
          if (next >= STEP_STOPS.length) return release(true);
          busy = true;
          index = next;
          // Closing the card again starts the next opening from film one.
          if (index === 0) slide = 0;
          tween(stop(index));
        }

        function tween(progress) {
          tl.tweenTo(progress * tl.duration(), {
            duration: 0.9,
            ease: 'power2.inOut',
            onComplete: function () {
              busy = false;
            }
          });
        }

        // Arrows (and dots) move between the films once the card is open,
        // whether or not the page is held.
        state.slideTo = function (i) {
          if (busy || tl.progress() < TOUCH_SLIDES[0] - 0.02 || i === slide) return;
          busy = true;
          slide = i;
          tween(TOUCH_SLIDES[i]);
        };
        dots.forEach(function (dot, i) {
          dot.addEventListener('click', function () { state.slideTo(i); });
        });

        return pin;
      }

      state.ctx = gsap.context(function () {
        gsap.set(media, { transformOrigin: '0 0' });
        gsap.set(textAnchor, { x: 0, xPercent: -50 });
        gsap.set(frames[0], { clipPath: FULL_CLIP });
        gsap.set([frames[1], frames[2]], { clipPath: 'polygon(100% 0%, 100% 0%, 101% 100%, 125% 100%)' });
        gsap.set(imgs[0], { scale: 1, xPercent: 0 });
        gsap.set([imgs[1], imgs[2]], { scale: 1.35, xPercent: 20 });
        gsap.set(texts, { opacity: 0, y: 30 });
        if (scrim) gsap.set(scrim, { opacity: 0 });

        function track(p) {
          section.classList.toggle('is-expanded', p > 0.12);
          setActive(p < 0.38 ? 0 : p < 0.7 ? 1 : 2);
        }

        // Touch screens step through the sequence instead of scrubbing it (see
        // stepThrough below), so a fling cannot carry the page past it.
        var tl = touch
          ? gsap.timeline({ paused: true, defaults: { ease: 'none' }, onUpdate: function () { track(tl.progress()); } })
          : gsap.timeline({
              defaults: { ease: 'none' },
              scrollTrigger: {
                trigger: section,
                pin: true,
                pinSpacing: true,
                anticipatePin: 1,
                start: 'top top',
                end: function () {
                  return '+=' + Math.round(stageH() * SCROLL_VIEWPORTS);
                },
                scrub: 0.8,
                invalidateOnRefresh: true,
                onUpdate: function (self) {
                  track(self.progress);
                }
              }
            });

        // Phase 1 (0 -> 0.2): the card opens to the full stage.
        tl.fromTo(frame,
          { clipPath: thumbClip },
          { clipPath: FULL_CLIP, duration: 0.2, ease: 'power2.inOut', immediateRender: true },
          0);
        tl.fromTo(media,
          { x: thumbX, y: thumbY, scale: thumbScale },
          { x: 0, y: 0, scale: 1, duration: 0.2, ease: 'power2.inOut', immediateRender: true },
          0);
        if (intro) tl.fromTo(intro, { opacity: 1, scale: 1 }, { opacity: 0, scale: 0.96, duration: 0.16, ease: 'power2.out' }, 0.02);
        if (scrim) tl.to(scrim, { opacity: 1, duration: 0.16, ease: 'power2.out' }, 0.08);
        tl.set(textAnchor, { x: function () { return copyX(0.72); } }, 0);
        tl.to(texts[0], { opacity: 1, y: 0, duration: 0.09, ease: 'power3.out' }, 0.16);

        // Phase 2 (0.2 -> 0.38): hold slide 1.
        tl.to(imgs[0], { scale: 1.04, duration: 0.18 }, 0.2);

        // Phase 3 (0.38 -> 0.54): wipe to slide 2, copy to centre.
        tl.to(frames[1], { clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)', duration: 0.16, ease: 'power2.inOut' }, 0.38);
        tl.to(imgs[1], { scale: 1, xPercent: 0, duration: 0.16, ease: 'power2.inOut' }, 0.38);
        tl.to(imgs[0], { scale: 1.3, xPercent: -20, duration: 0.16, ease: 'power2.inOut' }, 0.38);
        tl.to(textAnchor, { x: function () { return copyX(0.5); }, duration: 0.16, ease: 'power2.inOut' }, 0.38);
        tl.to(texts[0], { opacity: 0, y: -20, duration: 0.08, ease: 'power2.in' }, 0.38);
        tl.fromTo(texts[1], { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.1, ease: 'power3.out' }, 0.44);

        // Phase 4 (0.54 -> 0.7): hold slide 2.
        tl.to(imgs[1], { scale: 1.04, duration: 0.16 }, 0.54);

        // Phase 5 (0.7 -> 0.86): wipe to slide 3, copy to the left.
        tl.to(frames[2], { clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)', duration: 0.16, ease: 'power2.inOut' }, 0.7);
        tl.to(imgs[2], { scale: 1, xPercent: 0, duration: 0.16, ease: 'power2.inOut' }, 0.7);
        tl.to(imgs[1], { scale: 1.3, xPercent: -20, duration: 0.16, ease: 'power2.inOut' }, 0.7);
        tl.to(textAnchor, { x: function () { return copyX(0.24); }, duration: 0.16, ease: 'power2.inOut' }, 0.7);
        tl.to(texts[1], { opacity: 0, y: -20, duration: 0.08, ease: 'power2.in' }, 0.7);
        tl.fromTo(texts[2], { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.1, ease: 'power3.out' }, 0.76);

        // Phase 6 (0.86 -> 1): slide 3 stays up until the pin releases.
        tl.to(imgs[2], { scale: 1.04, duration: 0.14 }, 0.86);

        state.trigger = touch ? stepThrough(tl) : tl.scrollTrigger;
      }, section);

      if (window.VirsaayMotion && window.VirsaayMotion.register) {
        window.VirsaayMotion.register(section, state.trigger);
      }

      setActive(0);
      section.classList.add('is-ready');

      if (window.VirsaayMotion && window.VirsaayMotion.refresh) {
        window.VirsaayMotion.refresh(60);
        window.VirsaayMotion.on('loader:done', function () {
          window.VirsaayMotion.refresh(60);
        });
      } else {
        ScrollTrigger.refresh();
      }
    });

    state.onResize = function () {
      if (window.innerWidth === state.width) return;
      state.width = window.innerWidth;
      setHeight();
    };
    window.addEventListener('resize', state.onResize);
  }

  function destroyCarousel(section) {
    var state = section && section._vcc;
    if (!state) return;
    if (state.ctx) state.ctx.revert();
    window.removeEventListener('resize', state.onResize);
    section._vcc = null;
  }

  function findIn(target) {
    if (!target) return null;
    if (target.hasAttribute && target.hasAttribute('data-virsaay-carousel')) return target;
    return target.querySelector ? target.querySelector('[data-virsaay-carousel]') : null;
  }

  function initAll() {
    var sections = document.querySelectorAll('[data-virsaay-carousel]');
    for (var i = 0; i < sections.length; i++) initCarousel(sections[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (evt) {
    initCarousel(findIn(evt.target));
  });

  document.addEventListener('shopify:section:unload', function (evt) {
    destroyCarousel(findIn(evt.target));
  });
})();
